import { debounce } from 'lodash'
import moment from 'moment-timezone'
import { useCallback, useEffect, useState } from 'react'
import { Alert, Spinner } from 'react-bootstrap'
import { Modal } from 'react-bootstrap'
import Select, { components } from 'react-select'
import { toast } from 'react-toastify'
import CurrencyComp from '../../../../components/currencyComponent/CurrencyComp'
import DateComp from '../../../../components/dateComponent/DateComp'
import DateRangeComp from '../../../../components/dateComponent/DateRangeComp'
import { LoadingComponent } from '../../../../components/loadingComponent/LoadingComponent'
import NoRecordFound from '../../../../components/noRecord/NoRecordFound'
import Pagination from '../../../../components/pagination/Pagination'
import RefreshComponent from '../../../../components/refreshComponent/RefreshComponent'
import SearchBarComponent from '../../../../components/searchBar/SearchBarComponent'
import TableHeading from '../../../../components/tableHeading/TableHeading'
import TableSettingMenu from '../../../../components/tableHeading/TableSettingMenu'
import SearchSelect from '../../../../components/textFields/SearchSelect'
import TextField from '../../../../components/textFields/TextField'
import { useGetAllUsersManagerAdminQuery } from '../../../../services/GeneralApi'
import { useGetSSToBCSyncFeedsReportDetailQuery } from '../../../../services/ReportAndAnalyticsApi'
import useColumnVisibility from '../../../customHooks/useColumnVisibility'
import labelKey from '../../../localization/label.json'
import tosterKey from '../../../localization/toster.json'

const { Option } = components

type OptionType = {
  value: string
  label: string
}

// Custom Option component to add checkboxes
const CheckboxOption = (props: any) => {
  return (
    <Option {...props} className='d-flex align-items-start gap-2'>
      <input type='checkbox' checked={props.isSelected} onChange={() => null} />{' '}
      <label>{props.label}</label>
    </Option>
  )
}

interface SSToBCSyncFeedsReportDetailModalProps {
  show: boolean
  handleClose: () => void
  initialReportType?: string
  initialStartDate?: string
  initialEndDate?: string
}

// Table column configuration
interface TableColumn {
  id: string
  label: string
  type?: 'currency' | 'date' | 'link' | 'text' | 'boolean'
  className?: string
  labelClassName?: string
  sortable?: boolean
}

const tableColumns: TableColumn[] = [
  { id: 'bcEmployeeId', label: 'BC Employee Id', labelClassName: 'text-primary text-center', className: 'ps-4 text-center', sortable: true },
  { id: 'expenseId', label: 'SS Expense Id', className: 'ps-4 text-center', sortable: true },
  { id: 'trackingId', label: 'SS Tracking Id', className: 'ps-4 text-center', sortable: true },
  { id: 'expenseStatus', label: 'SS Expense Status', className: 'ps-4 text-center', sortable: true },
  { id: 'ssReportTotalAmount', label: 'SS Report Total Amount', labelClassName: 'text-end', className: 'text-end', sortable: true, type: 'currency' },
  { id: 'bcSyncReportTotalAmount', label: 'BC Sync Report Total Amount', labelClassName: 'text-primary text-end', className: 'text-end', sortable: true, type: 'currency' },
  { id: 'bcExpenseCreated', label: 'BC Expense Created', labelClassName: 'text-primary text-center', className: 'ps-4 text-center', type: 'boolean', sortable: true },
  { id: 'bcExpenseCreatedDateTime', label: 'BC Expense Created Date', labelClassName: 'text-primary text-center', type: 'date', className: 'w-100px text-center', sortable: true },
  { id: 'bcExpenseCreatedMsg', label: 'BC Expense Created Msg', labelClassName: 'text-primary text-center', className: 'text-center', sortable: true },
  { id: 'bcInvoiceOrCreditMemoNo', label: 'BC Invoice Number', labelClassName: 'text-primary text-center', className: 'ps-4 text-center', sortable: true },
  { id: 'bcRespPayToVendorNumber', label: 'BC Pay To Vendor No', labelClassName: 'text-primary text-center', className: 'text-center', sortable: true },
  { id: 'vendorName', label: 'BC Pay To Vendor Name', labelClassName: 'text-primary text-center', className: 'text-center', sortable: true },
  { id: 'expenseCategory', label: 'SS Expense Category', className: 'ps-4 text-center', sortable: true },
  { id: 'paymentMethod', label: 'SS Payment Method', className: 'ps-4 text-center', sortable: true },
  { id: 'itemDate', label: 'SS Item Date', className: 'ps-4 text-center', sortable: true, type: 'date' },
  { id: 'itemAmount', label: 'SS Item Amount', className: 'ps-4 text-center', sortable: true, type: 'currency' },
  { id: 'itemDescription', label: 'Item Description', className: 'ps-4 text-center', sortable: true },
  { id: 'expenseCategoryName', label: 'Expense Category Name', className: 'ps-4 text-center', sortable: true },
  { id: 'reportDate', label: 'Report Date', className: 'ps-4 text-center', sortable: true, type: 'date' },
  { id: 'ssEmployeeId', label: 'SS Employee ID', className: 'ps-4 text-center', sortable: true },
  { id: 'userFullName', label: 'User Full Name', className: 'ps-4 text-center', sortable: true },
]

const SSToBCSyncFeedsReportDetailModal = ({
  show,
  handleClose,
  initialReportType = '',
  initialStartDate = '',
  initialEndDate = ''
}: SSToBCSyncFeedsReportDetailModalProps) => {
  const { hiddenColumns, handleToggleColumn } = useColumnVisibility({ pageName: 'sSToBCSyncFeedsReportDetail' })

  const currentDate = moment().format('MM/DD/YYYY')
  const sevenDaysAgo = moment().subtract(6, 'days').format('MM/DD/YYYY')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchStr, setSearchStr] = useState('')
  const [immediateSearchStr, setImmediateSearchStr] = useState('')
  const [filterByBCEmployeeId, setFilterByBCEmployeeId] = useState('')
  const [localFilterByBCEmployeeId, setLocalFilterByBCEmployeeId] = useState('')
  const [filterByReportType, setFilterByReportType] = useState(initialReportType || '')
  const [localFilterByReportType, setLocalFilterByReportType] = useState(initialReportType || '')
  const [sortBy, setSortBy] = useState('trackingId')
  const [sortOrder, setSortOrder] = useState('desc')
  const [sortedColumn, setSortedColumn] = useState('trackingId')
  const [filterByStartDate, setFilterByStartDate] = useState(initialStartDate || sevenDaysAgo)
  const [filterByEndDate, setFilterByEndDate] = useState(initialEndDate || currentDate)
  const [localFilterByStartDate, setLocalFilterByStartDate] = useState(initialStartDate || sevenDaysAgo)
  const [localFilterByEndDate, setLocalFilterByEndDate] = useState(initialEndDate || currentDate)
  const [filterByUserIds, setFilterByUserIds] = useState<string[]>([])
  const [localFilterByUserIds, setLocalFilterByUserIds] = useState<string[]>([])
  const [selectedUserIds, setSelectedUserIds] = useState<OptionType[]>([])
  const [isApplyingFilters, setIsApplyingFilters] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Report type options
  const reportTypeOptions: OptionType[] = [
    { value: '1', label: 'Submitted' },
    { value: '2', label: 'Manager Approved' },
    { value: '3', label: 'Ready for BC Sync (Pending)' },
    { value: '4', label: 'Sync Failed' },
    { value: '5', label: 'Created in BC (Pending AP)' },
    { value: '6', label: 'AP Approved' },
    { value: '7', label: 'Paid' },
  ]

  // Get report type label
  const getReportTypeLabel = (type: string): string => {
    const types: { [key: string]: string } = {
      '1': 'Submitted',
      '2': 'Manager Approved',
      '3': 'Ready for BC Sync (Pending)',
      '4': 'Sync Failed',
      '5': 'Created in BC (Pending AP)',
      '6': 'AP Approved',
      '7': 'Paid'
    }
    return types[type] || 'Unknown'
  }

  // Sync initial props with state when modal opens or props change
  useEffect(() => {
    if (show) {
      // Set report type from props
      if (initialReportType) {
        setFilterByReportType(initialReportType)
        setLocalFilterByReportType(initialReportType)
      }

      // Set dates from props
      if (initialStartDate) {
        setFilterByStartDate(initialStartDate)
        setLocalFilterByStartDate(initialStartDate)
      }
      if (initialEndDate) {
        setFilterByEndDate(initialEndDate)
        setLocalFilterByEndDate(initialEndDate)
      }
    }
  }, [show, initialReportType, initialStartDate, initialEndDate])

  // Fetch users for dropdown
  const { data: getUserByManager } = useGetAllUsersManagerAdminQuery('')

  // Fetch API data with new endpoint
  const { data, isFetching, refetch, isSuccess } = useGetSSToBCSyncFeedsReportDetailQuery({
    startDate: filterByStartDate,
    endDate: filterByEndDate,
    reportType: filterByReportType || initialReportType,
    searchStr: searchStr,
    bcEmployeeId: filterByBCEmployeeId,
    userIds: filterByUserIds.length > 0 ? filterByUserIds.join(',') : '""',
    sortBy: sortBy,
    sortOrder: sortOrder,
    pageNumber: pageNumber,
    pageSize: pageSize,
  }, { skip: !show })

  // Handle page size change
  const handlePageSizeChange = (e: any) => {
    const newSize = parseInt(e.target.value)
    setPageSize(newSize)
    setPageNumber(1)
  }

  // Pagination
  const handlePageClick = (e: any) => {
    setPageNumber(e.selected + 1)
  }

  // Sort handler
  const handleSort = (property: string) => {
    setSortBy(property)
    if (sortedColumn === property) {
      setSortOrder((prevSortOrder) => (prevSortOrder === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortedColumn(property)
      setSortOrder('asc')
    }
  }

  // Search handlers
  const handleSearchChange = (e: any) => {
    const { value } = e.target
    setImmediateSearchStr(value)
    delayedSearch(value)
    setPageNumber(1)
  }

  const delayedSearch = useCallback(
    debounce((searchValue) => {
      setSearchStr(searchValue)
    }, 500),
    []
  )

  const handleBCEmployeeIdChange = (e: any) => {
    const { value } = e.target
    setLocalFilterByBCEmployeeId(value)
  }

  // Handle report type change - expects OptionType or null
  const handleReportTypeChange = (selected: OptionType | null) => {
    setLocalFilterByReportType(selected?.value || '')
  }

  const userIdsOptions: OptionType[] = getUserByManager?.result?.map((userByManager: any) => ({
    value: userByManager.id,
    label: userByManager.value,
  })) || []

  // Handle user ids change - expects OptionType[]
  const handleUserIdsChange = (selected: readonly OptionType[]) => {
    setSelectedUserIds(selected as OptionType[])
    setLocalFilterByUserIds(selected?.map((user) => user.value) || [])
  }

  // Date range handler
  const handleDateRangeChange = (event: any, picker: any) => {
    const startDateFormatted = moment(picker.startDate).format('MM/DD/YYYY')
    const endDateFormatted = moment(picker.endDate).format('MM/DD/YYYY')

    setLocalFilterByStartDate(startDateFormatted)
    setLocalFilterByEndDate(endDateFormatted)
  }

  const handleCancelDateSelection = (event: any, picker: any) => {
    setLocalFilterByStartDate('')
    setLocalFilterByEndDate('')
  }

  // Apply filters
  const applyFilters = () => {
    setIsApplyingFilters(true)
    setFilterByStartDate(localFilterByStartDate)
    setFilterByEndDate(localFilterByEndDate)
    setFilterByBCEmployeeId(localFilterByBCEmployeeId)
    setFilterByReportType(localFilterByReportType)
    setFilterByUserIds(localFilterByUserIds)
    setPageNumber(1)
    setTimeout(() => setIsApplyingFilters(false), 500)
  }

  // Reset filters
  const resetFilters = () => {
    setFilterByStartDate('')
    setFilterByEndDate('')
    setFilterByBCEmployeeId('')
    setFilterByReportType('')
    setFilterByUserIds([])
    setSearchStr('')
    setImmediateSearchStr('')
    setLocalFilterByStartDate('')
    setLocalFilterByEndDate('')
    setLocalFilterByBCEmployeeId('')
    setLocalFilterByReportType('')
    setLocalFilterByUserIds([])
    setSelectedUserIds([])
    setPageNumber(1)
  }

  // Refresh handler
  const handleRefresh = () => {
    refetch().then(() => {
      if (isSuccess) {
        toast.success(tosterKey.dataRefreshedSuccesfully)
      }
    })
  }

  // Loading effect
  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  // Trigger refetch when filters change
  useEffect(() => {
    if (show && (searchStr !== '' || filterByBCEmployeeId !== '' || filterByReportType !== '' || filterByUserIds.length > 0)) {
      refetch()
    }
  }, [searchStr, filterByBCEmployeeId, filterByReportType, filterByUserIds, refetch, show])

  const tableData = data?.result?.data || []
  const totalRecords = data?.result?.totalRecords || 0

  // Helper function to render cell content based on column type
  const renderCellContent = (item: any, column: TableColumn) => {
    const value = item[column.id]

    if (value === undefined || value === null) return '-'
    if (value === '') return '-'

    switch (column.type) {
      case 'currency':
        return <CurrencyComp amount={value} />
      case 'boolean':
        return value ? 'Yes' : 'No'
      case 'date':
        return <DateComp formattedDate={value} />
      default:
        return value
    }
  }

  // Helper function to render cell with proper styling
  const renderCell = (item: any, column: TableColumn) => {
    const isCurrency = column.type === 'currency'
    const isDate = column.type === 'date'

    if (isCurrency) {
      return (
        <td className='text-end pe-4'>
          {renderCellContent(item, column)}
        </td>
      )
    }

    if (isDate) {
      return (
        <td className='text-center'>
          {renderCellContent(item, column)}
        </td>
      )
    }

    return (
      <td className={column.className || 'ps-4 text-center'}>
        {renderCellContent(item, column)}
      </td>
    )
  }

  // Get visible columns based on hiddenColumns
  const visibleColumns = hiddenColumns.length > 0 ? tableColumns.filter(col => hiddenColumns.includes(col.id)) : tableColumns;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      fullscreen={true}
      centered
      className="modal-compact"
    >
      <Modal.Header closeButton className="py-2">
        <Modal.Title className="fs-5">
          <i className="fas fa-info-circle me-2"></i>
          SS to BC Sync Detail - {getReportTypeLabel(filterByReportType || initialReportType)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3">
        {/* Filter Row - More Compact */}
         <Alert variant="primary">
          <p className='mb-0 fs-6'>Displays detailed synchronization records between SpendSail and Business Central, including successful, pending, and failed sync transactions with their current processing status. This page helps administrators monitor integration health, investigate sync failures, and verify synchronized expense records based on the selected filters.</p>
        </Alert>
        <div className='row align-items-end g-2 mb-3'>
          <div className='col-md-3 col-lg-2 col-xl-2'>
            <label className='form-label fs-7 fw-bold mb-1'>Search:</label>
            <SearchBarComponent
              placeholder='Search...'
              value={immediateSearchStr}
              onChange={handleSearchChange}
              className='w-100'
            />
          </div>
          <div className='col-md-3 col-lg-2 col-xl-2'>
            <label className='form-label fs-7 fw-bold mb-1'>{labelKey.bcEmployeeId}:</label>
            <SearchBarComponent
              placeholder='BC Employee ID'
              value={localFilterByBCEmployeeId}
              onChange={handleBCEmployeeIdChange}
              className='w-100'
            />
          </div>
          <div className='col-md-3 col-lg-2 col-xl-2'>
            <label className='form-label fs-7 fw-bold mb-1'>{labelKey.users}:</label>
            <Select
              isMulti
              options={userIdsOptions}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ Option: CheckboxOption }}
              onChange={(selected) => handleUserIdsChange(selected as OptionType[])}
              value={selectedUserIds}
              placeholder='Search User...'
              className='filter-valueContainer'
            />
          </div>
          <div className='col-md-3 col-lg-2 col-xl-2'>
            <label className='form-label fs-7 fw-bold mb-1'>Report Type:</label>
            <Select
              isMulti={false}
              options={reportTypeOptions}
              placeholder='Select Report Type'
              value={reportTypeOptions.find(option => option.value === localFilterByReportType) || null}
              onChange={handleReportTypeChange}
              className='filter-valueContainer'
            />
          </div>
          <div className='col-md-3 col-lg-2 col-xl-2'>
            <DateRangeComp
              startDate={localFilterByStartDate || moment()}
              endDate={localFilterByEndDate || moment()}
              onApply={handleDateRangeChange}
              onCancel={handleCancelDateSelection}
              value={
                localFilterByStartDate && localFilterByEndDate
                  ? `${localFilterByStartDate} - ${localFilterByEndDate}`
                  : 'MM/DD/YYYY'
              }
            />
          </div>
          <div className='col-md-3 col-lg-2 col-xl-2'>
            <div className='d-flex align-items-end h-100 gap-2'>
              <button className='btn btn-danger btn-sm' onClick={resetFilters}>
                <i className="fas fa-undo me-1"></i>
                Reset
              </button>
              <button className='btn btn-primary btn-sm' onClick={applyFilters}>
                {isApplyingFilters ? (
                  <>
                    <Spinner
                      as='span'
                      animation='border'
                      size='sm'
                      role='status'
                      aria-hidden='true'
                      className='me-1'
                    />
                    Applying...
                  </>
                ) : (
                  <>
                    <i className="fas fa-filter me-1"></i>
                    Apply
                  </>
                )}
              </button>
              <RefreshComponent onClick={handleRefresh} />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className='col-md-12 p-4 border rounded-2 border-gray-300'>
          <div className='table-responsive  table-height'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 table-sm'>
              <thead className='position-sticky top-0'>
                <tr className='fw-bold text-muted'>
                  {visibleColumns.map((column) => (
                    <TableHeading
                      key={column.id}
                      label={column.label}
                      sortedColumn={sortedColumn}
                      sortOrder={sortOrder}
                      handleSort={handleSort}
                      columnId={column.id}
                      className={column.className}
                      labelClassName={column.labelClassName}
                    />
                  ))}
                  <th className='text-end rounded-end pe-2'>
                    <TableSettingMenu>
                      {tableColumns.map((column) => (
                        <TextField
                          key={column.id}
                          rightLabel={column.label}
                          type='checkbox'
                          onChange={() => handleToggleColumn(column.id)}
                          checked={hiddenColumns.includes(column.id)}
                          fieldClass='mb-2'
                        />
                      ))}
                    </TableSettingMenu>
                  </th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && (
                  <>
                    {tableData.length > 0 ? (
                      <>
                        {tableData.map((item: any, index: any) => (
                          <tr key={index}>
                            {visibleColumns.map((column) => renderCell(item, column))}
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td colSpan={visibleColumns.length + 1}>
                          <NoRecordFound />
                        </td>
                      </tr>
                    )}
                  </>
                )}
                {isLoading && (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className='text-center'>
                      <LoadingComponent />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className='mt-2'>
            <Pagination
              totalResult={totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1}
              toResult={totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)}
              ofResult={totalRecords}
              onChange={handlePageSizeChange}
              pageSize={pageSize}
              onPageChange={handlePageClick}
              pageCount={data?.result?.totalPages || 0}
              currentPage={pageNumber}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="py-2">
        <button className="btn btn-secondary btn-sm" onClick={handleClose}>
          <i className="fas fa-times me-1"></i>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default SSToBCSyncFeedsReportDetailModal